import React, { useState, useEffect } from 'react';
import { Truck, Store, Package, Loader2, Settings, DollarSign, Gift, Globe, Plane } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const ShippingManagementTab = () => {
  const [loading, setLoading] = useState(false);
  const [intlLoading, setIntlLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [shippingStats, setShippingStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [selectedSellerSettings, setSelectedSellerSettings] = useState(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Bulk update form - Domestic
  const [shippingCost, setShippingCost] = useState('185');
  const [selectedSeller, setSelectedSeller] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Bulk update form - International
  const [intlShippingCost, setIntlShippingCost] = useState('250');
  const [intlSelectedSeller, setIntlSelectedSeller] = useState('all');
  const [intlSelectedCategory, setIntlSelectedCategory] = useState('');
  
  // Seller settings form
  const [sellerShippingForm, setSellerShippingForm] = useState({
    default_shipping_cost: 185,
    free_shipping_threshold: '',
    express_shipping_cost: '',
    international_shipping_cost: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setStatsLoading(true);
      const [statsRes, sellersRes] = await Promise.all([
        admin.getShippingStats(),
        admin.allSellers()
      ]);
      setShippingStats(statsRes.data);
      setSellers(sellersRes.data.sellers || []);
    } catch (error) {
      console.error('Failed to fetch shipping stats:', error);
      toast.error('Kargo istatistikleri yüklenemedi');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBulkShippingUpdate = async () => {
    const cost = parseFloat(shippingCost);
    if (isNaN(cost) || cost < 0) {
      toast.error('Geçerli bir kargo ücreti girin');
      return;
    }

    try {
      setLoading(true);
      const data = {
        shipping_cost: cost,
        seller_id: selectedSeller === 'all' ? null : selectedSeller,
        category: selectedCategory || null
      };
      
      const response = await admin.bulkShippingUpdate(data);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Bulk shipping update failed:', error);
      toast.error(error.response?.data?.detail || 'Kargo güncellemesi başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkIntlShippingUpdate = async () => {
    const cost = parseFloat(intlShippingCost);
    if (isNaN(cost) || cost < 0) {
      toast.error('Geçerli bir yurtdışı kargo ücreti girin');
      return;
    }

    try {
      setIntlLoading(true);
      const data = {
        international_shipping_cost: cost,
        seller_id: intlSelectedSeller === 'all' ? null : intlSelectedSeller,
        category: intlSelectedCategory || null
      };
      
      const response = await admin.bulkShippingUpdate(data);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Bulk international shipping update failed:', error);
      toast.error(error.response?.data?.detail || 'Yurtdışı kargo güncellemesi başarısız');
    } finally {
      setIntlLoading(false);
    }
  };

  const openSellerSettings = async (sellerId) => {
    try {
      const response = await admin.getSellerShippingSettings(sellerId);
      setSelectedSellerSettings(response.data);
      setSellerShippingForm({
        default_shipping_cost: response.data.shipping_settings?.default_shipping_cost || 185,
        free_shipping_threshold: response.data.shipping_settings?.free_shipping_threshold || '',
        express_shipping_cost: response.data.shipping_settings?.express_shipping_cost || '',
        international_shipping_cost: response.data.shipping_settings?.international_shipping_cost || ''
      });
      setSettingsDialogOpen(true);
    } catch (error) {
      toast.error('Satıcı ayarları yüklenemedi');
    }
  };

  const handleSaveSellerSettings = async () => {
    if (!selectedSellerSettings) return;
    
    try {
      setLoading(true);
      const data = {
        seller_id: selectedSellerSettings.seller.id,
        default_shipping_cost: parseFloat(sellerShippingForm.default_shipping_cost) || 0,
        free_shipping_threshold: sellerShippingForm.free_shipping_threshold ? parseFloat(sellerShippingForm.free_shipping_threshold) : null,
        express_shipping_cost: sellerShippingForm.express_shipping_cost ? parseFloat(sellerShippingForm.express_shipping_cost) : null,
        international_shipping_cost: sellerShippingForm.international_shipping_cost ? parseFloat(sellerShippingForm.international_shipping_cost) : null
      };
      
      const response = await admin.updateSellerShippingSettings(selectedSellerSettings.seller.id, data);
      toast.success(response.data.message);
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="shipping-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Kargo Yönetimi
          </h2>
          <p className="text-muted-foreground mt-1">
            Satıcıların ürünlerine kargo ücreti belirleyin
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Toplam Ürün
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {shippingStats?.total_products || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Kargo Ücreti Olan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {shippingStats?.products_with_shipping || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ortalama Kargo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {shippingStats?.avg_shipping || 0} ₺
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Aktif Satıcı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {Object.keys(shippingStats?.seller_stats || {}).length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Update Form - Domestic */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Toplu Yurtiçi Kargo Ücreti Güncelleme
          </CardTitle>
          <CardDescription>
            Seçilen satıcı ve kategorilere sabit yurtiçi kargo ücreti uygulayın
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Shipping Cost */}
            <div className="space-y-2">
              <Label htmlFor="shipping">Yurtiçi Kargo Ücreti (TL)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="shipping"
                  type="number"
                  min="0"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  className="pl-10"
                  placeholder="185"
                  data-testid="shipping-cost-input"
                />
              </div>
            </div>

            {/* Seller Filter */}
            <div className="space-y-2">
              <Label htmlFor="seller">Satıcı</Label>
              <Select value={selectedSeller} onValueChange={setSelectedSeller}>
                <SelectTrigger data-testid="seller-select">
                  <Store className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Satıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Satıcılar</SelectItem>
                  {sellers.map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.store_name || seller.name || seller.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label htmlFor="category">Kategori (Opsiyonel)</Label>
              <Input
                id="category"
                type="text"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                placeholder="Örn: Etek, Ayakkabı..."
                data-testid="category-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleBulkShippingUpdate}
              disabled={loading}
              size="lg"
              className="min-w-[200px]"
              data-testid="apply-shipping-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <Truck className="w-4 h-4 mr-2" />
                  Yurtiçi Kargo Uygula
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Update Form - International */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Toplu Yurtdışı Kargo Ücreti Güncelleme
          </CardTitle>
          <CardDescription>
            Seçilen satıcı ve kategorilere sabit yurtdışı kargo ücreti uygulayın
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* International Shipping Cost */}
            <div className="space-y-2">
              <Label htmlFor="intl-shipping" className="flex items-center gap-2">
                <Plane className="w-4 h-4 text-blue-600" />
                Yurtdışı Kargo Ücreti (TL)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="intl-shipping"
                  type="number"
                  min="0"
                  value={intlShippingCost}
                  onChange={(e) => setIntlShippingCost(e.target.value)}
                  className="pl-10"
                  placeholder="250"
                  data-testid="intl-shipping-cost-input"
                />
              </div>
            </div>

            {/* Seller Filter */}
            <div className="space-y-2">
              <Label htmlFor="intl-seller">Satıcı</Label>
              <Select value={intlSelectedSeller} onValueChange={setIntlSelectedSeller}>
                <SelectTrigger data-testid="intl-seller-select">
                  <Store className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Satıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Satıcılar</SelectItem>
                  {sellers.map((seller) => (
                    <SelectItem key={seller.id} value={seller.id}>
                      {seller.store_name || seller.name || seller.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label htmlFor="intl-category">Kategori (Opsiyonel)</Label>
              <Input
                id="intl-category"
                type="text"
                value={intlSelectedCategory}
                onChange={(e) => setIntlSelectedCategory(e.target.value)}
                placeholder="Örn: Etek, Ayakkabı..."
                data-testid="intl-category-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleBulkIntlShippingUpdate}
              disabled={intlLoading}
              size="lg"
              className="min-w-[200px] bg-blue-600 hover:bg-blue-700"
              data-testid="apply-intl-shipping-btn"
            >
              {intlLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4 mr-2" />
                  Yurtdışı Kargo Uygula
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seller Stats Table */}
      {shippingStats?.seller_stats && Object.keys(shippingStats.seller_stats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Satıcı Bazlı Kargo İstatistikleri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Satıcı</th>
                    <th className="text-left py-3 px-4 font-medium">Ürün Sayısı</th>
                    <th className="text-left py-3 px-4 font-medium">Ort. Kargo</th>
                    <th className="text-left py-3 px-4 font-medium">Min/Max</th>
                    <th className="text-left py-3 px-4 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(shippingStats.seller_stats).map(([sellerId, stats]) => {
                    const seller = sellers.find(s => s.id === sellerId);
                    return (
                      <tr key={sellerId} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <span className="font-medium">
                            {seller?.store_name || seller?.name || seller?.email || sellerId.slice(0, 8) + '...'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="secondary">{stats.product_count}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="default" className="bg-blue-600">
                            {stats.avg_shipping?.toFixed(2) || 0} ₺
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {stats.min_shipping?.toFixed(2) || 0} - {stats.max_shipping?.toFixed(2) || 0} ₺
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSellerSettings(sellerId)}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Ayarlar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seller Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Satıcı Kargo Ayarları
            </DialogTitle>
          </DialogHeader>
          
          {selectedSellerSettings && (
            <div className="space-y-4">
              <Alert>
                <Store className="w-4 h-4" />
                <AlertDescription>
                  <strong>{selectedSellerSettings.seller.store_name || selectedSellerSettings.seller.email}</strong>
                  <br />
                  <span className="text-sm text-muted-foreground">
                    {selectedSellerSettings.stats.product_count} ürün
                  </span>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Varsayılan Kargo Ücreti (TL)</Label>
                  <Input
                    type="number"
                    value={sellerShippingForm.default_shipping_cost}
                    onChange={(e) => setSellerShippingForm({...sellerShippingForm, default_shipping_cost: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-green-600" />
                    Ücretsiz Kargo Limiti (TL)
                  </Label>
                  <Input
                    type="number"
                    value={sellerShippingForm.free_shipping_threshold}
                    onChange={(e) => setSellerShippingForm({...sellerShippingForm, free_shipping_threshold: e.target.value})}
                    placeholder="Örn: 500 (500 TL üzeri ücretsiz)"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Ekspres Kargo Ücreti (TL)</Label>
                  <Input
                    type="number"
                    value={sellerShippingForm.express_shipping_cost}
                    onChange={(e) => setSellerShippingForm({...sellerShippingForm, express_shipping_cost: e.target.value})}
                    placeholder="Opsiyonel"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Uluslararası Kargo Ücreti (TL)</Label>
                  <Input
                    type="number"
                    value={sellerShippingForm.international_shipping_cost}
                    onChange={(e) => setSellerShippingForm({...sellerShippingForm, international_shipping_cost: e.target.value})}
                    placeholder="Opsiyonel"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                  İptal
                </Button>
                <Button onClick={handleSaveSellerSettings} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Kaydet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingManagementTab;
